import { clsx } from 'clsx';
import { Fragment } from 'react';
import { ChevronRight, Globe } from 'lucide-react';

import type { ScopedListRow } from '../model/types';

export type ScopedTreeViewerProps = {
  rows: ReadonlyArray<ScopedListRow>;
  height?: number;
  width?: number | string;
  selection?: string;
  onSelectRowId?: (rowId: string) => void;
  /**
   * Override the center "main" content of a row. Useful for domain-specific rows.
   * If not provided (or returns null), the default icon+label rendering is used.
   */
  renderMain?: (row: ScopedListRow) => React.ReactNode;
  renderActions?: (row: ScopedListRow) => React.ReactNode;
  /** Wrap the entire row element (e.g. with a ContextMenu). */
  renderRowWrapper?: (row: ScopedListRow, children: React.ReactNode) => React.ReactNode;
};

export function ScopedTreeViewer({
  rows,
  height = 480,
  width = '100%',
  selection,
  onSelectRowId,
  renderMain,
  renderActions,
  renderRowWrapper,
}: ScopedTreeViewerProps) {
  return (
    <div style={{ width, maxHeight: height }} className="overflow-auto">
      <div className="flex flex-col gap-px px-0.5 py-0.5">
        {rows.map((row) => {
          const isSelected = selection === row.id;
          const main = renderMain?.(row) ?? null;
          const actions = renderActions?.(row) ?? null;

          const rowElement = (
            <div
              className={clsx(
                'group flex h-5 items-center gap-1 rounded px-1 text-[11px] font-semibold',
                isSelected && 'bg-muted/60 text-foreground',
                !isSelected && 'text-muted-foreground hover:bg-muted/40'
              )}
              style={{ paddingLeft: 4 + row.depth * 6 }}
              onClick={() => onSelectRowId?.(row.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectRowId?.(row.id);
                }
              }}
            >
              {main ?? (
                <div className="flex min-w-0 flex-1 items-center gap-1 truncate font-medium">
                  {row.kind === 'patient' ? (
                    <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
                  ) : row.kind === 'problem' ? (
                    <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                  ) : null}
                  <div className="min-w-0 flex-1 truncate">{row.label}</div>
                </div>
              )}
              {actions}
            </div>
          );

          return (
            <Fragment key={row.id}>
              {renderRowWrapper ? renderRowWrapper(row, rowElement) : rowElement}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
